import { Link } from 'react-router-dom'
import { Band, BannerSlider, BlogCard, Btn, PullQuote, SectionHeading, Slider, SliderNav, SplitPanels, useText } from '@kkf/design'
import { BLOG_POSTS_HOME } from '../data/blog'
import { BANNER, HOME_ABOUT, HOME_BLOG_HEADING, HOME_DONATE_CTA, HOME_OFFERS, HOME_QUOTE } from '../data/home'

/**
 * Home page — the only page using the `withTopBar` header variant.
 *
 * **Reworked 2026-09-02.** It was nine sections and four of them were the template's:
 * a row of generic charity "features" (education, healthcare, livelihood, water — none of
 * them what the Foundation does), a project grid duplicating the nav, a lorem-ipsum
 * testimonial slider under an invented reviewer, and a second contact form. All four went.
 *
 * **Two more went on 2026-09-19** and it is now five sections:
 *
 *   banner → what we are → the blog → two offers → give
 *
 * The quotation that opened the page is gone from here; the same words still open the
 * resort's home page, which is where they were always the better fit — that page has no
 * other voice on it before the place describes itself. And the Partners band ("Members Get
 * 5% Off Everything King of Kings Does") is gone: the ask now sits on `/donations`, at the
 * top of the page somebody is already on to do something, rather than as the penultimate
 * band of a page they are scrolling past.
 *
 * That leaves `Band tone="ink"` as the page's one closing call. It was always the quieter
 * of the two, and with `Cta`'s full-bleed photograph and 80px title no longer above it,
 * the last word on the page is a single ask rather than two competing ones.
 */
/**
 * How many blog cards "Stories From The Work" shows at once.
 *
 * **Four across from 992px up, not three.** The section is a sample of the blog and four
 * cards fill the container at the card's natural size; three leave each one wider than its
 * own photograph wants to be.
 *
 * 992 is the breakpoint that matters, and the first attempt set four at 1400: most desktops
 * are between 1280 and 1440, so the common case went on showing three and the change looked
 * like it had not been made.
 *
 * Exported so `tests/home-and-donations.test.tsx` can pin the count. Swiper lays nothing out
 * in jsdom — every slide renders and none of them has a width — so the map is the only thing
 * a test can read, and it is the thing that was wrong.
 */
export const BLOG_SLIDER_BREAKPOINTS = {
  576: { slidesPerView: 2 },
  992: { slidesPerView: 4, spaceBetween: 20 },
  1680: { slidesPerView: 4, spaceBetween: 26 },
}

export function HomePage() {
  const s = useText()
  const a = HOME_ABOUT

  return (
    <>
      <BannerSlider
        slides={BANNER.slides.map((slide) => ({
          image: slide.image,
          alt: s(slide.alt),
          content: (
            <div className="wow animate__fadeInUp">
              {/* `--lg` sets the eyebrow at display size: it carries the organisation's
                  name, and at body size that read as a caption. */}
              <span className="ul-banner-sub-title ul-banner-sub-title--lg ul-section-sub-title">
                {s(slide.subTitle)}
              </span>
              <h1 className="ul-banner-title">{s(slide.title)}</h1>
              <p className="ul-banner-descr">{s(slide.descr)}</p>
              <div className="ul-banner-btns">
                <Btn to={slide.ctaTo} icon="fast-forward-double-right-arrows-symbol">
                  {s(slide.ctaLabel)}
                </Btn>
              </div>
            </div>
          ),
        }))}
        thumbs={BANNER.thumbs}
        prevArrow={BANNER.prevArrow}
        nextArrow={BANNER.nextArrow}
      />

      {/* QUOTE — between the banner and the About band.

          `PullQuote` had no consumer until this page mounted it. `audit:reuse` had been
          carrying it as "retained without a consumer" since 2026-09-19, when the resort's
          home page dropped the two bands that used it; the note there said to delete the
          component and its `.ul-pullquote` rules together if nothing ever mounted it.
          Something does.

          **The words are a labelled placeholder, awaiting Brad Smith's quotation
          (2026-09-21)** — see `data/home`. `--pending` is what says so on the page: the
          band keeps its place in the page's rhythm, and nothing here reads as something
          a real person said. Remove the flag in the same commit as the real words. */}
      <section className="ul-section-spacing wow animate__fadeInUp">
        <div className="ul-container">
          <PullQuote
            text={s(HOME_QUOTE.text)}
            attribution={HOME_QUOTE.attribution}
            {...(HOME_QUOTE.pending ? { className: 'ul-pullquote--pending' } : {})}
          />
        </div>
      </section>

      {/* ABOUT */}
      <section className="ul-about ul-about-2 ul-section-spacing wow animate__fadeInUp">
        <div className="ul-container">
          <div className="row row-cols-md-2 row-cols-1 align-items-center gy-4 ul-about-row">
            <div className="col">
              <div className="ul-about-imgs ul-about-2-img">
                <div className="img-wrapper">
                  <img src={a.image} alt={s(a.imageAlt)} />
                </div>
                {/* The badge is a link now — it names the three strands, and the page that
                    explains them is one click away. `.ul-about-2-stat` is absolutely
                    positioned, so the anchor carries the class rather than wrapping it. */}
                <Link to={a.stat.to} className="ul-about-2-stat">
                  <span className="number">{a.stat.number}</span>
                  <span className="txt">{a.stat.txt}</span>
                </Link>
              </div>
            </div>

            {/* txt */}
            <div className="col">
              <div className="ul-about-txt">
                <span className="ul-section-sub-title ul-section-sub-title--2">{s(a.subTitle)}</span>
                <h2 className="ul-section-title">{s(a.title)}</h2>
                <p className="ul-section-descr">{s(a.descr)}</p>
                <div className="ul-about-bottom ul-about-2-bottom">
                  {a.blocks.map((block) => (
                    <div className="ul-about-2-bottom-block" key={s(block.title)}>
                      {/* Inline SVG, not an <img>: the icons are painted with tokens so
                          they follow the theme. See `data/royal-icons.tsx`. */}
                      <div className="ul-about-2-bottom-block-icon">{block.icon}</div>
                      <div className="ul-about-2-bottom-block-txt">
                        <h3 className="ul-about-2-bottom-block-title">{s(block.title)}</h3>
                        <p className="ul-about-2-bottom-block-descr">{s(block.descr)}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Btn to={a.ctaTo} icon="fast-forward-double-right-arrows-symbol">
                  {s(a.ctaLabel)}
                </Btn>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BLOG — the slider that used to carry services; it moves slowly on purpose */}
      <section className="ul-blogs-2 ul-services ul-section-spacing overflow-hidden">
        <div className="ul-container">
          <SectionHeading
            subTitle={HOME_BLOG_HEADING.subTitle}
            title={s(HOME_BLOG_HEADING.title)}
            aside={
              <div className="d-flex align-items-center gap-3">
                <Btn to={HOME_BLOG_HEADING.ctaTo} icon="fast-forward-double-right-arrows-symbol">
                  {s(HOME_BLOG_HEADING.ctaLabel)}
                </Btn>
                <SliderNav className="ul-services-slider-nav" extraClassName="position-static" />
              </div>
            }
          />
          <Slider
            className="ul-services-slider"
            trailingClassName="overflow-visible"
            options={{
              slidesPerView: 1,
              // Slow on purpose: 9s between slides and a 2s glide, so a reader has time
              // to finish a card before it moves.
              autoplay: { delay: 9000, disableOnInteraction: false },
              speed: 2000,
              spaceBetween: 15,
              navigation: {
                prevEl: '.ul-services-slider-nav .prev',
                nextEl: '.ul-services-slider-nav .next',
              },
              breakpoints: BLOG_SLIDER_BREAKPOINTS,
            }}
            slides={BLOG_POSTS_HOME.map((post, i) => (
              <BlogCard {...post} key={`${post.image}-${i}`} />
            ))}
          />
        </div>
      </section>

      {/* THE TWO OFFERS — free space, and mentoring. The free ones come first. */}
      <SplitPanels
        left={{
          title: s(HOME_OFFERS.left.title),
          descr: s(HOME_OFFERS.left.descr),
          list: HOME_OFFERS.left.list.map(s),
          action: (
            <Btn href={HOME_OFFERS.left.ctaHref} icon="fast-forward-double-right-arrows-symbol">
              {s(HOME_OFFERS.left.ctaLabel)}
            </Btn>
          ),
        }}
        right={{
          title: s(HOME_OFFERS.right.title),
          descr: s(HOME_OFFERS.right.descr),
          list: HOME_OFFERS.right.list.map(s),
          action: (
            <Btn to={HOME_OFFERS.right.ctaTo} icon="fast-forward-double-right-arrows-symbol">
              {s(HOME_OFFERS.right.ctaLabel)}
            </Btn>
          ),
        }}
      />

      {/* GIVE — the last word */}
      <Band tone="ink" ornament containerClassName="wow animate__fadeInUp">
        <SectionHeading
          className="justify-content-center text-center"
          subTitle={s(HOME_DONATE_CTA.subTitle)}
          title={s(HOME_DONATE_CTA.title)}
        />
        <p className="ul-band-lead">{s(HOME_DONATE_CTA.lead)}</p>
        <div className="d-flex flex-wrap justify-content-center gap-3 mt-4">
          <Btn to={HOME_DONATE_CTA.primaryCta.to} icon="fast-forward-double-right-arrows-symbol">
            {s(HOME_DONATE_CTA.primaryCta.label)}
          </Btn>
          <Btn
            to={HOME_DONATE_CTA.secondaryCta.to}
            className="ul-btn--2"
            icon="fast-forward-double-right-arrows-symbol"
          >
            {s(HOME_DONATE_CTA.secondaryCta.label)}
          </Btn>
        </div>
      </Band>
    </>
  )
}
